import { moveColumnOrder, normalizeColumnsLayout } from 'src/helpers/columnsLayoutUtils';
import { dividerSpacingToBlockSpacing } from 'src/helpers/blockSpacing';
import { normalizeLocalizedString } from 'src/helpers/localizedStringUtils';
import {
  createDefaultHomeHeroSectionBlocks,
  createEmptyPageBlock,
} from 'src/helpers/pageBlockFactory';
import {
  applyPageBlockPatch,
  assignBlockSortOrders,
  hasActiveVisibilityRules,
  sortPageBlocks,
} from 'src/helpers/pageContentUtils';
import { IHeroSettings } from 'src/interfaces/HeroSettings';
import { IBasePageContent } from 'src/interfaces/PageContent';
import {
  IButtonLinkBlock,
  IColumnsBlock,
  IDividerBlock,
  IPageBlock,
  ITextBlock,
  LegacyPageBlockType,
  PageBlockType,
  PageBlockZone,
} from 'src/interfaces/PageBlock';

export const HERO_ROOT_DROP_ID = 'hero-root';
export const BODY_ROOT_DROP_ID = 'body-root';

const LEGACY_TYPE_MAP: Record<LegacyPageBlockType, PageBlockType> = {
  heroLogo: 'logo',
  heroCalendar: 'calendar',
  heroCountdown: 'countdown',
  heroButton: 'button',
};

export interface IPageDocument {
  blocks: IPageBlock[];
  heroSettings?: IHeroSettings;
}

type BlockLocation =
  | { kind: 'zone'; zone: PageBlockZone; index: number }
  | { kind: 'column'; columnsBlockId: string; columnIndex: number; index: number };

function isLegacyBlockType(type: string): type is LegacyPageBlockType {
  return type in LEGACY_TYPE_MAP;
}

function columnsDroppablePrefix(columnsBlockId: string): string {
  return `columns-${columnsBlockId}`;
}

export function columnsDroppableId(columnsBlockId: string, columnIndex: number): string {
  return `${columnsDroppablePrefix(columnsBlockId)}-col-${columnIndex}`;
}

export function layoutColSortableId(columnsBlockId: string, columnIndex: number): string {
  return `${columnsDroppablePrefix(columnsBlockId)}-layout-col-${columnIndex}`;
}

function isLayoutColId(id: string, columnsBlockId: string): boolean {
  return id.startsWith(`${columnsDroppablePrefix(columnsBlockId)}-layout-col-`);
}

function layoutColIndexFromId(id: string, columnsBlockId: string): number {
  return Number(id.slice(`${columnsDroppablePrefix(columnsBlockId)}-layout-col-`.length));
}

function normalizeBlockType(block: IPageBlock): IPageBlock {
  const raw = block as IPageBlock & {
    type: string;
    heroFrostedBackground?: boolean;
  };
  let next: IPageBlock = block;
  if (isLegacyBlockType(raw.type)) {
    next = { ...block, type: LEGACY_TYPE_MAP[raw.type] } as IPageBlock;
  }
  if (next.type === 'text') {
    const text = next as ITextBlock & { heroFrostedBackground?: boolean };
    if ('heroFrostedBackground' in text) {
      const { heroFrostedBackground, ...rest } = text;
      next = {
        ...rest,
        frostedBackground: heroFrostedBackground,
      } as ITextBlock;
    }
  }
  if (next.type === 'button') {
    const button = next as IButtonLinkBlock & { href?: string | Record<string, string> };
    next = {
      ...button,
      href: normalizeLocalizedString(button.href),
    };
  }
  if (next.type === 'divider') {
    const divider = next as IDividerBlock & { dividerSpacing?: string };
    const { dividerSpacing, spacing, ...rest } = divider;
    if (!spacing && dividerSpacing) {
      next = {
        ...rest,
        spacing: dividerSpacingToBlockSpacing(dividerSpacing),
      } as IDividerBlock;
    } else if (dividerSpacing) {
      next = rest as IDividerBlock;
    }
  }
  if (next.type === 'columns') {
    const layout = normalizeColumnsLayout(next as IColumnsBlock, { minColumns: 2, maxColumns: 4 });
    if (layout) {
      next = {
        ...next,
        ...layout,
        columns: layout.columns.map(col => ({
          ...col,
          blocks: col.blocks.map(b => stripZone(normalizeBlockType(b))),
        })),
      } as IColumnsBlock;
    }
  }
  return next;
}

function stripZone(block: IPageBlock): IPageBlock {
  const { zone: _z, ...rest } = block as IPageBlock & { zone?: PageBlockZone };
  return rest as IPageBlock;
}

function normalizeTopLevelBlock(block: IPageBlock, zone: PageBlockZone): IPageBlock {
  const normalized = normalizeBlockType(block);
  return { ...normalized, zone };
}

function normalizeHeroSettings(settings: IHeroSettings | undefined): IHeroSettings | undefined {
  if (!settings) return undefined;
  const backgroundImageUrl = settings.backgroundImageUrl?.trim() || undefined;
  const backgroundImageFileId = settings.backgroundImageFileId?.trim() || undefined;
  if (
    !backgroundImageUrl &&
    !backgroundImageFileId &&
    settings.backgroundAlignH == null &&
    settings.backgroundAlignV == null
  ) {
    return undefined;
  }
  return {
    ...settings,
    backgroundImageUrl,
    backgroundImageFileId,
    backgroundAlignH: settings.backgroundAlignH ?? 'center',
    backgroundAlignV: settings.backgroundAlignV ?? 'center',
  };
}

export function normalizePageDocument(
  raw: IPageDocument | IBasePageContent | undefined,
): IPageDocument {
  if (!raw) return { blocks: [] };

  const heroSettings = normalizeHeroSettings(raw.heroSettings);
  const blocks = (raw.blocks ?? []).map(b =>
    normalizeTopLevelBlock(b, b.zone === 'hero' ? 'hero' : 'body'),
  );

  const normalizedBlocks = blocks.map(b => {
    if (b.type === 'columns') return normalizeBlockType(b) as IColumnsBlock;
    return b;
  });

  return {
    blocks: assignBlockSortOrders(normalizedBlocks),
    heroSettings,
  };
}

function getBlocksByZone(doc: IPageDocument, zone: PageBlockZone): IPageBlock[] {
  return sortPageBlocks(doc.blocks.filter(b => (b.zone ?? 'body') === zone));
}

export function getHeroBlocks(doc: IPageDocument): IPageBlock[] {
  return getBlocksByZone(doc, 'hero');
}

export function getBodyBlocks(doc: IPageDocument): IPageBlock[] {
  return getBlocksByZone(doc, 'body');
}

export function findBlockLocation(doc: IPageDocument, blockId: string): BlockLocation | null {
  for (const top of doc.blocks) {
    if (top.id === blockId) {
      const zone = top.zone ?? 'body';
      const index = getBlocksByZone(doc, zone).findIndex(b => b.id === blockId);
      return { kind: 'zone', zone, index: index >= 0 ? index : 0 };
    }
    if (top.type === 'columns') {
      for (let ci = 0; ci < top.columns.length; ci++) {
        const idx = top.columns[ci]!.blocks.findIndex(b => b.id === blockId);
        if (idx >= 0) {
          return { kind: 'column', columnsBlockId: top.id, columnIndex: ci, index: idx };
        }
      }
    }
  }
  return null;
}

function mergeZoneBlocks(hero: IPageBlock[], body: IPageBlock[]): IPageBlock[] {
  return [...assignBlockSortOrders(hero), ...assignBlockSortOrders(body)];
}

function updateTopLevelBlocks(doc: IPageDocument, blocks: IPageBlock[]): IPageDocument {
  return { ...doc, blocks: assignBlockSortOrders(blocks) };
}

function updateColumnsBlock(
  doc: IPageDocument,
  columnsBlockId: string,
  updater: (block: IColumnsBlock) => IColumnsBlock,
): IPageDocument {
  return updateTopLevelBlocks(
    doc,
    doc.blocks.map(b => {
      if (b.id !== columnsBlockId || b.type !== 'columns') return b;
      return updater(b as IColumnsBlock);
    }),
  );
}

export function movePageBlock(
  doc: IPageDocument,
  activeId: string,
  overId: string,
): IPageDocument {
  for (const top of doc.blocks) {
    if (top.type === 'columns' && isLayoutColId(activeId, top.id)) {
      const from = layoutColIndexFromId(activeId, top.id);
      let to = from;
      if (isLayoutColId(overId, top.id)) {
        to = layoutColIndexFromId(overId, top.id);
      }
      return updateColumnsBlock(doc, top.id, block =>
        moveColumnOrder(block, from, to),
      );
    }
  }

  const activeLoc = findBlockLocation(doc, activeId);
  if (!activeLoc) return doc;

  if (overId === HERO_ROOT_DROP_ID || overId === BODY_ROOT_DROP_ID) {
    const zone: PageBlockZone = overId === HERO_ROOT_DROP_ID ? 'hero' : 'body';
    return moveBlockToZone(doc, activeId, zone, null);
  }

  for (const top of doc.blocks) {
    if (top.type !== 'columns') continue;
    const prefix = columnsDroppablePrefix(top.id);
    for (let i = 0; i < top.columns.length; i++) {
      if (overId === `${prefix}-col-${i}` || overId === layoutColSortableId(top.id, i)) {
        return moveBlockToColumn(doc, activeId, top.id, i, top.columns[i]!.blocks.length);
      }
    }
    const overCol = top.columns.findIndex(col => col.blocks.some(b => b.id === overId));
    if (overCol >= 0) {
      const targetIndex = top.columns[overCol]!.blocks.findIndex(b => b.id === overId);
      return moveBlockToColumn(doc, activeId, top.id, overCol, targetIndex);
    }
  }

  const overLoc = findBlockLocation(doc, overId);
  if (overLoc?.kind === 'zone') {
    return moveBlockToZone(doc, activeId, overLoc.zone, overLoc.index);
  }

  return doc;
}

function removeBlockFromDocument(doc: IPageDocument, blockId: string): {
  doc: IPageDocument;
  block: IPageBlock | null;
} {
  const loc = findBlockLocation(doc, blockId);
  if (!loc) return { doc, block: null };

  if (loc.kind === 'zone') {
    const block = doc.blocks.find(b => b.id === blockId) ?? null;
    const hero = getBlocksByZone(doc, 'hero').filter(b => b.id !== blockId);
    const body = getBlocksByZone(doc, 'body').filter(b => b.id !== blockId);
    return { doc: { ...doc, blocks: mergeZoneBlocks(hero, body) }, block };
  }

  let removed: IPageBlock | null = null;
  const next = updateColumnsBlock(doc, loc.columnsBlockId, colBlock => {
    const columns = colBlock.columns.map((col, ci) => {
      if (ci !== loc.columnIndex) return col;
      removed = col.blocks[loc.index] ?? null;
      return {
        ...col,
        blocks: col.blocks.filter((_, bi) => bi !== loc.index),
      };
    });
    return {
      ...colBlock,
      columns: columns.map(col => ({ ...col, blocks: assignBlockSortOrders(col.blocks) })),
    };
  });
  return { doc: next, block: removed };
}

function insertBlock(
  doc: IPageDocument,
  block: IPageBlock,
  target: BlockLocation,
): IPageDocument {
  if (target.kind === 'zone') {
    const hero = getBlocksByZone(doc, 'hero');
    const body = getBlocksByZone(doc, 'body');
    const list = target.zone === 'hero' ? [...hero] : [...body];
    list.splice(target.index, 0, { ...block, zone: target.zone });
    const blocks =
      target.zone === 'hero'
        ? mergeZoneBlocks(list, body)
        : mergeZoneBlocks(hero, list);
    return { ...doc, blocks };
  }

  return updateColumnsBlock(doc, target.columnsBlockId, colBlock => {
    const columns = colBlock.columns.map((col, ci) => {
      if (ci !== target.columnIndex) return col;
      const blocks = [...col.blocks];
      blocks.splice(target.index, 0, stripZone(block));
      return { ...col, blocks: assignBlockSortOrders(blocks) };
    });
    return { ...colBlock, columns };
  });
}

function moveBlockToZone(
  doc: IPageDocument,
  activeBlockId: string,
  zone: PageBlockZone,
  insertBeforeIndex: number | null,
): IPageDocument {
  const { doc: without, block } = removeBlockFromDocument(doc, activeBlockId);
  if (!block) return doc;

  const hero = getBlocksByZone(without, 'hero');
  const body = getBlocksByZone(without, 'body');
  const list = zone === 'hero' ? [...hero] : [...body];
  const index = insertBeforeIndex == null ? list.length : insertBeforeIndex;
  list.splice(index, 0, { ...block, zone });

  return {
    ...without,
    blocks:
      zone === 'hero' ? mergeZoneBlocks(list, body) : mergeZoneBlocks(hero, list),
  };
}

function moveBlockToColumn(
  doc: IPageDocument,
  activeBlockId: string,
  columnsBlockId: string,
  columnIndex: number,
  targetIndex: number,
): IPageDocument {
  const { doc: without, block } = removeBlockFromDocument(doc, activeBlockId);
  if (!block) return doc;

  return insertBlock(without, block, {
    kind: 'column',
    columnsBlockId,
    columnIndex,
    index: targetIndex,
  });
}

export function moveBlockOrder(
  doc: IPageDocument,
  blockId: string,
  direction: 'up' | 'down',
): IPageDocument {
  const loc = findBlockLocation(doc, blockId);
  if (!loc) return doc;

  const delta = direction === 'up' ? -1 : 1;

  if (loc.kind === 'zone') {
    const zone = loc.zone;
    const zoneBlocks = getBlocksByZone(doc, zone);
    const target = loc.index + delta;
    if (target < 0 || target >= zoneBlocks.length) return doc;
    const reordered = [...zoneBlocks];
    const tmp = reordered[loc.index]!;
    reordered[loc.index] = reordered[target]!;
    reordered[target] = tmp;
    const other =
      zone === 'hero' ? getBlocksByZone(doc, 'body') : getBlocksByZone(doc, 'hero');
    const blocks =
      zone === 'hero'
        ? [...assignBlockSortOrders(reordered), ...assignBlockSortOrders(other)]
        : [...assignBlockSortOrders(other), ...assignBlockSortOrders(reordered)];
    return { ...doc, blocks };
  }

  return updateColumnsBlock(doc, loc.columnsBlockId, colBlock => {
    const columns = colBlock.columns.map((col, ci) => {
      if (ci !== loc.columnIndex) return col;
      const blocks = [...col.blocks];
      const target = loc.index + delta;
      if (target < 0 || target >= blocks.length) return col;
      const tmp = blocks[loc.index]!;
      blocks[loc.index] = blocks[target]!;
      blocks[target] = tmp;
      return { ...col, blocks: assignBlockSortOrders(blocks) };
    });
    return { ...colBlock, columns };
  });
}

export type AddBlockLocation =
  | { kind: 'zone'; zone: PageBlockZone; insertIndex?: number }
  | { kind: 'column'; columnsBlockId: string; columnIndex: number; insertIndex?: number };

export function addPageBlock(
  doc: IPageDocument,
  location: AddBlockLocation,
  type: PageBlockType,
  options?: { trackMapId?: string; columnCount?: 2 | 3 | 4 },
): IPageDocument {
  if (type === 'columns' && location.kind === 'column') return doc;

  if (location.kind === 'zone') {
    const list = getBlocksByZone(doc, location.zone);
    const insertIndex = location.insertIndex ?? list.length;
    const block = {
      ...createEmptyPageBlock(type, insertIndex, options),
      zone: location.zone,
    };
    return insertBlock(doc, block, {
      kind: 'zone',
      zone: location.zone,
      index: Math.min(Math.max(0, insertIndex), list.length),
    });
  }

  const colBlock = doc.blocks.find(
    b => b.id === location.columnsBlockId && b.type === 'columns',
  ) as IColumnsBlock | undefined;
  if (!colBlock) return doc;
  const col = colBlock.columns[location.columnIndex];
  if (!col) return doc;
  const insertIndex = location.insertIndex ?? col.blocks.length;
  const block = createEmptyPageBlock(type, insertIndex, options);
  return insertBlock(doc, block, {
    kind: 'column',
    columnsBlockId: location.columnsBlockId,
    columnIndex: location.columnIndex,
    index: Math.min(Math.max(0, insertIndex), col.blocks.length),
  });
}

export function removePageBlock(doc: IPageDocument, blockId: string): IPageDocument {
  return removeBlockFromDocument(doc, blockId).doc;
}

export function updatePageBlock(
  doc: IPageDocument,
  blockId: string,
  patch: Partial<IPageBlock>,
): IPageDocument {
  const loc = findBlockLocation(doc, blockId);
  if (!loc) return doc;

  if (loc.kind === 'zone') {
    return updateTopLevelBlocks(
      doc,
      doc.blocks.map(b => (b.id === blockId ? applyPageBlockPatch(b, patch) : b)),
    );
  }

  return updateColumnsBlock(doc, loc.columnsBlockId, colBlock => ({
    ...colBlock,
    columns: colBlock.columns.map((col, ci) =>
      ci !== loc.columnIndex
        ? col
        : {
            ...col,
            blocks: col.blocks.map(b =>
              b.id === blockId ? applyPageBlockPatch(b, patch) : b,
            ),
          },
    ),
  }));
}

export function forEachPageBlock(
  doc: IPageDocument,
  visit: (block: IPageBlock, ctx: { zone?: PageBlockZone; inColumn: boolean }) => void,
): void {
  for (const top of doc.blocks) {
    visit(top, { zone: top.zone, inColumn: false });
    if (top.type === 'columns') {
      for (const col of top.columns) {
        for (const nested of col.blocks) {
          visit(nested, { zone: top.zone, inColumn: true });
        }
      }
    }
  }
}

export function documentHasSponsorsBlock(doc: IPageDocument): boolean {
  let found = false;
  forEachPageBlock(doc, block => {
    if (block.type === 'sponsors') found = true;
  });
  return found;
}

function sanitizeBlockForPersistence(block: IPageBlock, isTopLevel: boolean): IPageBlock {
  const next = { ...block };
  if (!hasActiveVisibilityRules(next.visibility)) {
    delete next.visibility;
  }
  if (!isTopLevel) {
    delete (next as IPageBlock & { zone?: PageBlockZone }).zone;
  }
  if (next.type === 'columns') {
    const columnsBlock = next as IColumnsBlock;
    return {
      ...columnsBlock,
      columns: columnsBlock.columns.map(col => ({
        ...col,
        blocks: col.blocks.map(b => sanitizeBlockForPersistence(b, false)),
      })),
    };
  }
  return next;
}

export function pageContentFromDocument(
  doc: IPageDocument,
  pageContent: IBasePageContent | undefined,
  name: string,
): IBasePageContent {
  const blocks = doc.blocks.map(b => sanitizeBlockForPersistence(b, true));
  const base: IBasePageContent = {
    name,
    blocks,
  };
  if (doc.heroSettings) {
    base.heroSettings = { ...doc.heroSettings };
  }
  if (pageContent?.id) {
    base.id = pageContent.id;
  }
  return base;
}

export function createDefaultHomeDocument(): IPageDocument {
  const { column0, column1 } = createDefaultHomeHeroSectionBlocks();
  const columnsBlock = createEmptyPageBlock('columns', 0, { columnCount: 2 }) as IColumnsBlock;
  columnsBlock.columns[0] = { ...columnsBlock.columns[0]!, blocks: column0 };
  columnsBlock.columns[1] = { ...columnsBlock.columns[1]!, blocks: column1 };
  return normalizePageDocument({
    name: 'home',
    blocks: [{ ...columnsBlock, zone: 'hero' }],
    heroSettings: {
      backgroundAlignH: 'center',
      backgroundAlignV: 'center',
    },
  });
}

export function isHeroSectionEmpty(doc: IPageDocument): boolean {
  const heroBlocks = getHeroBlocks(doc);
  if (!heroBlocks.length && !doc.heroSettings?.backgroundImageUrl) return true;
  return heroBlocks.every(block => {
    if (block.type === 'columns') {
      return block.columns.every(col => !col.blocks.length);
    }
    if (block.type === 'text') {
      return !(['en', 'hu', 'ro'] as const).some(lang => block.i18n[lang]?.trim());
    }
    return false;
  });
}
